<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { NButton, NCard, NBadge, NTag } from 'naive-ui'
import {
    LANGUAGES, getCurrentLanguage, setCurrentLanguage,
    getLocaleFromPreferred
} from '@/services/language'
import { dt } from '@/utils/i18n'
import lawyerDashboardImage from '@/assets/images/philippe-oursel-pWhx6q22d_I-unsplash.jpg'
import verificationImage from '@/assets/images/marek-studzinski-iv-Iau6sLgw-unsplash.jpg'
import scheduleImage from '@/assets/images/medical-563427_640.jpg'
import analyticsImage from '@/assets/images/vitaly-gariev-8WYkI3cEZm8-unsplash.jpg'

const router = useRouter()
const { t, locale } = useI18n()

onMounted(() => {
    locale.value = getLocaleFromPreferred(getCurrentLanguage())
})

const handleLawyerRegister = () => {
    router.push({
        name: 'register',
        query: { role: 'lawyer' }
    })
}

const handleLawyerLogin = () => {
    router.push({
        name: 'login',
        query: { role: 'lawyer' }
    })
}
</script>

<template>
    <div class="lawyer-page">
        <!-- Hero Section for Lawyers -->
        <section class="lawyer-hero">
            <div class="container">
                <div class="hero-grid">
                    <div class="hero-content">
                        <h1 class="hero-title">{{ dt('lawyersHome.heroTitle') }}</h1>
                        <p class="hero-subtitle">{{ dt('lawyersHome.heroSubtitle') }}</p>

                        <div class="cta-buttons">
                            <NButton type="primary" size="large" class="cta-button-primary"
                                @click="handleLawyerRegister">
                                {{ dt('lawyersHome.getStarted') }}
                            </NButton>
                            <NButton size="large" class="cta-button-secondary" @click="handleLawyerLogin">
                                {{ dt('lawyersHome.lawyerLogin') }}
                            </NButton>
                        </div>

                        <div class="hero-stats">
                            <div class="stat-item">
                                <div class="stat-number">50,000+</div>
                                <div class="stat-label">{{ dt('lawyersHome.stats.clients') }}</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-number">12</div>
                                <div class="stat-label">{{ dt('lawyersHome.stats.languages') }}</div>
                            </div>
                            <div class="stat-item">
                                <div class="stat-number">100%</div>
                                <div class="stat-label">{{ dt('lawyersHome.stats.free') }}</div>
                            </div>
                        </div>
                    </div>

                    <div class="hero-image">
                        <img :src="lawyerDashboardImage" alt="Lawyer Dashboard" class="dashboard-preview" />
                    </div>
                </div>
            </div>
        </section>

        <!-- Why Join Section -->
        <section class="why-join">
            <div class="container">
                <h2 class="section-title">{{ dt('lawyersHome.whyJoinTitle') }}</h2>
                <div class="benefits-grid">
                    <div class="benefit-card">
                        <div class="benefit-icon">⚖️</div>
                        <h3>{{ dt('lawyersHome.benefits.reach.title') }}</h3>
                        <p>{{ dt('lawyersHome.benefits.reach.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">🗣️</div>
                        <h3>{{ dt('lawyersHome.benefits.multilingual.title') }}</h3>
                        <p>{{ dt('lawyersHome.benefits.multilingual.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">📊</div>
                        <h3>{{ dt('lawyersHome.benefits.profile.title') }}</h3>
                        <p>{{ dt('lawyersHome.benefits.profile.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">⭐</div>
                        <h3>{{ dt('lawyersHome.benefits.reputation.title') }}</h3>
                        <p>{{ dt('lawyersHome.benefits.reputation.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">📅</div>
                        <h3>{{ dt('lawyersHome.benefits.consultations.title') }}</h3>
                        <p>{{ dt('lawyersHome.benefits.consultations.description') }}</p>
                    </div>
                    <div class="benefit-card">
                        <div class="benefit-icon">🔒</div>
                        <h3>{{ dt('lawyersHome.benefits.verification.title') }}</h3>
                        <p>{{ dt('lawyersHome.benefits.verification.description') }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Features for Lawyers -->
        <section class="lawyer-features">
            <div class="container">
                <h2 class="section-title">{{ dt('lawyersHome.featuresTitle') }}</h2>

                <!-- Feature 1: Profile Management -->
                <div class="feature-showcase">
                    <div class="feature-content">
                        <h3 class="feature-title">{{ dt('lawyersHome.features.profile.title') }}</h3>
                        <ul class="feature-list">
                            <li>✓ {{ dt('lawyersHome.features.profile.item1') }}</li>
                            <li>✓ {{ dt('lawyersHome.features.profile.item2') }}</li>
                            <li>✓ {{ dt('lawyersHome.features.profile.item3') }}</li>
                            <li>✓ {{ dt('lawyersHome.features.profile.item4') }}</li>
                        </ul>
                    </div>
                    <div class="feature-image">
                        <img :src="verificationImage" alt="Profile Management" />
                    </div>
                </div>

                <!-- Feature 2: Schedule Management -->
                <div class="feature-showcase reverse">
                    <div class="feature-content">
                        <h3 class="feature-title">{{ dt('lawyersHome.features.schedule.title') }}</h3>
                        <ul class="feature-list">
                            <li>✓ {{ dt('lawyersHome.features.schedule.item1') }}</li>
                            <li>✓ {{ dt('lawyersHome.features.schedule.item2') }}</li>
                            <li>✓ {{ dt('lawyersHome.features.schedule.item3') }}</li>
                            <li>✓ {{ dt('lawyersHome.features.schedule.item4') }}</li>
                        </ul>
                    </div>
                    <div class="feature-image">
                        <img :src="scheduleImage" alt="Schedule Management" />
                    </div>
                </div>

                <!-- Feature 3: Client Engagement -->
                <div class="feature-showcase">
                    <div class="feature-content">
                        <h3 class="feature-title">{{ dt('lawyersHome.features.engagement.title') }}</h3>
                        <ul class="feature-list">
                            <li>✓ {{ dt('lawyersHome.features.engagement.item1') }}</li>
                            <li>✓ {{ dt('lawyersHome.features.engagement.item2') }}</li>
                            <li>✓ {{ dt('lawyersHome.features.engagement.item3') }}</li>
                            <li>✓ {{ dt('lawyersHome.features.engagement.item4') }}</li>
                        </ul>
                    </div>
                    <div class="feature-image">
                        <div class="coming-soon-badge">
                            <NBadge value="Coming Soon" type="warning" />
                        </div>
                        <img :src="analyticsImage" alt="Client Engagement" />
                    </div>
                </div>
            </div>
        </section>

        <!-- How It Works for Lawyers -->
        <section class="how-it-works-lawyers">
            <div class="container">
                <h2 class="section-title">{{ dt('lawyersHome.howItWorksTitle') }}</h2>
                <div class="steps-grid">
                    <div class="step-card">
                        <div class="step-number">1</div>
                        <h4>{{ dt('lawyersHome.steps.register.title') }}</h4>
                        <p>{{ dt('lawyersHome.steps.register.description') }}</p>
                    </div>
                    <div class="step-card">
                        <div class="step-number">2</div>
                        <h4>{{ dt('lawyersHome.steps.profile.title') }}</h4>
                        <p>{{ dt('lawyersHome.steps.profile.description') }}</p>
                    </div>
                    <div class="step-card">
                        <div class="step-number">3</div>
                        <h4>{{ dt('lawyersHome.steps.verify.title') }}</h4>
                        <p>{{ dt('lawyersHome.steps.verify.description') }}</p>
                    </div>
                    <div class="step-card">
                        <div class="step-number">4</div>
                        <h4>{{ dt('lawyersHome.steps.connect.title') }}</h4>
                        <p>{{ dt('lawyersHome.steps.connect.description') }}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Language Coverage -->
        <section class="languages">
            <div class="container">
                <h2 class="section-title">{{ dt('lawyersHome.languagesTitle') }}</h2>
                <p class="languages-subtitle">{{ dt('lawyersHome.languagesSubtitle') }}</p>
                <div class="language-tags">
                    <NTag v-for="lang in LANGUAGES" :key="lang.value" type="success" size="large" class="lang-tag">
                        {{ lang.label }}
                    </NTag>
                </div>
            </div>
        </section>

        <!-- Final CTA -->
        <section class="final-cta">
            <div class="container">
                <div class="cta-content">
                    <h2>{{ dt('lawyersHome.finalCta.title') }}</h2>
                    <p>{{ dt('lawyersHome.finalCta.subtitle') }}</p>
                    <div class="cta-buttons">
                        <NButton type="primary" size="large" class="cta-button-large" @click="handleLawyerRegister">
                            {{ dt('lawyersHome.finalCta.button') }}
                        </NButton>
                    </div>
                    <p class="cta-note">{{ dt('lawyersHome.finalCta.note') }}</p>
                </div>
            </div>
        </section>

        <!-- For Clients Link -->
        <section class="for-clients-link">
            <div class="container">
                <p>{{ dt('lawyersHome.lookingForLawyer') }}
                    <router-link to="/" class="client-link">{{ dt('lawyersHome.goToClientSite') }}</router-link>
                </p>
            </div>
        </section>
    </div>
</template>

<style scoped>
.lawyer-page {
    min-height: 100vh;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
}

/* Lawyer-themed Hero */
.lawyer-hero {
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
    color: white;
    padding: 5rem 0;
}

.hero-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    align-items: center;
}

.hero-content {
    text-align: left;
}

.hero-title {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
}

.hero-subtitle {
    font-size: 1.4rem;
    margin-bottom: 2.5rem;
    opacity: 0.95;
    line-height: 1.6;
}

.cta-buttons {
    display: flex;
    gap: 1rem;
    margin-bottom: 3rem;
}

.cta-button-primary {
    background: #d4af37 !important;
    border: none;
    padding: 0 2.5rem;
    font-size: 1.1rem;
}

.cta-button-secondary {
    background: transparent;
    border: 2px solid white;
    color: white;
    padding: 0 2.5rem;
    font-size: 1.1rem;
}

.cta-button-secondary:hover {
    background: rgba(255, 255, 255, 0.1);
}

.hero-stats {
    display: flex;
    gap: 3rem;
}

.stat-item {
    text-align: center;
}

.stat-number {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 0.5rem;
}

.stat-label {
    font-size: 0.9rem;
    opacity: 0.9;
}

.dashboard-preview {
    max-width: 100%;
    border-radius: 15px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

/* Why Join Section */
.why-join {
    padding: 5rem 0;
    background: #f8f9fa;
}

.section-title {
    text-align: center;
    font-size: 2.8rem;
    margin-bottom: 1rem;
    color: #2c3e50;
}

.benefits-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
}

.benefit-card {
    background: white;
    padding: 2.5rem;
    border-radius: 12px;
    text-align: center;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    border-top: 4px solid #d4af37;
}

.benefit-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 30px rgba(212, 175, 55, 0.2);
}

.benefit-icon {
    font-size: 3.5rem;
    margin-bottom: 1.5rem;
}

.benefit-card h3 {
    color: #2c3e50;
    margin-bottom: 1rem;
    font-size: 1.4rem;
}

.benefit-card p {
    color: #7f8c8d;
    line-height: 1.6;
}

/* Lawyer Features */
.lawyer-features {
    padding: 5rem 0;
}

.feature-showcase {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
    margin: 4rem 0;
}

.feature-showcase.reverse {
    direction: rtl;
}

.feature-showcase.reverse .feature-content {
    direction: ltr;
}

.feature-title {
    font-size: 2rem;
    color: #2c3e50;
    margin-bottom: 1.5rem;
}

.feature-list {
    list-style: none;
    padding: 0;
}

.feature-list li {
    padding: 0.8rem 0;
    font-size: 1.1rem;
    color: #4a5568;
    border-bottom: 1px solid #e2e8f0;
}

.feature-image {
    position: relative;
}

.feature-image img {
    max-width: 100%;
    border-radius: 12px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.coming-soon-badge {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
}

/* How It Works for Lawyers */
.how-it-works-lawyers {
    padding: 5rem 0;
    background: linear-gradient(135deg, #f6f9fc 0%, #edf2f7 100%);
}

.steps-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
}

.step-card {
    background: white;
    text-align: center;
    padding: 2.5rem;
    border-radius: 12px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
}

.step-number {
    width: 50px;
    height: 50px;
    background: #d4af37;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
    font-size: 1.5rem;
    font-weight: bold;
}

.step-card h4 {
    color: #2c3e50;
    margin-bottom: 1rem;
    font-size: 1.3rem;
}

.step-card p {
    color: #7f8c8d;
    line-height: 1.6;
}

/* Languages Section */
.languages {
    padding: 5rem 0;
    background: #f8f9fa;
}

.languages-subtitle {
    text-align: center;
    font-size: 1.2rem;
    color: #7f8c8d;
    margin-bottom: 2rem;
}

.language-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
    max-width: 800px;
    margin: 0 auto;
}

.lang-tag {
    font-size: 1.1rem;
}

/* Final CTA */
.final-cta {
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
    padding: 80px 20px;
    text-align: center;
}

.final-cta .container {
    max-width: 1200px;
    margin: 0 auto;
}

.final-cta .cta-content {
    max-width: 800px;
    margin: 0 auto;
}

.final-cta h2 {
    font-size: 2.5rem;
    font-weight: 700;
    color: white;
    margin-bottom: 1rem;
    text-align: center;
}

.final-cta p {
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 2rem;
    text-align: center;
}

.cta-buttons {
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 2rem 0;
}

.cta-button-large {
    padding: 16px 48px !important;
    font-size: 18px !important;
    font-weight: 600 !important;
    background-color: #d4af37 !important;
    border: none !important;
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    transition: all 0.3s ease !important;
    cursor: pointer;
}

.cta-button-large:hover {
    background-color: #b8941f !important;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2) !important;
}

.cta-note {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
    margin-top: 1rem;
    text-align: center;
}

/* For Clients Link */
.for-clients-link {
    padding: 2rem 0;
    text-align: center;
    background: #1a252f;
    color: white;
}

.client-link {
    color: #d4af37;
    text-decoration: underline;
    font-weight: 600;
}

.client-link:hover {
    color: #b8941f;
}

/* Responsive */
@media (max-width: 768px) {
    .final-cta {
        padding: 60px 20px;
    }

    .final-cta h2 {
        font-size: 2rem;
    }

    .final-cta p {
        font-size: 1.1rem;
    }

    .cta-button-large {
        padding: 14px 32px !important;
        font-size: 16px !important;
        width: 100%;
        max-width: 300px;
    }

    .hero-grid,
    .feature-showcase,
    .feature-showcase.reverse {
        grid-template-columns: 1fr;
    }

    .hero-title {
        font-size: 2.2rem;
    }

    .cta-buttons {
        flex-direction: column;
    }

    .hero-stats {
        flex-direction: column;
        gap: 1.5rem;
    }

    .benefits-grid {
        grid-template-columns: 1fr;
    }
}
</style>